import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import "./dashboard.css";

const PRIMARY_BTN_STYLE = (active) => ({
  alignItems: "center",
  backgroundColor: active ? "#605DEC" : "#D5D3CE",
  border: "none",
  borderRadius: 8,
  color: "#FAF7F2",
  display: "inline-flex",
  fontFamily: "Inter, sans-serif",
  fontSize: 18,
  fontWeight: 700,
  height: 48,
  justifyContent: "center",
  letterSpacing: "0.25px",
  lineHeight: "26px",
  padding: "0 16px",
});

const SECONDARY_BTN_STYLE = {
  alignItems: "center",
  backgroundColor: "white",
  border: "1px solid #605DEC",
  borderRadius: 8,
  color: "#605DEC",
  display: "inline-flex",
  fontFamily: "Inter, sans-serif",
  fontSize: 18,
  fontWeight: 700,
  height: 48,
  justifyContent: "center",
  letterSpacing: "0.25px",
  lineHeight: "26px",
  padding: "0 16px",
  width: 180,
};

const Checkbox = ({ checked, onChange, onDark = false }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    aria-checked={checked}
    role="checkbox"
    style={{
      alignItems: "center",
      background: checked ? "#0DA662" : "transparent",
      border: `2px solid ${checked ? "#0DA662" : (onDark ? "white" : "#D3D3F8")}`,
      borderRadius: 4,
      cursor: "pointer",
      display: "flex",
      height: 24,
      justifyContent: "center",
      padding: 0,
      width: 24,
    }}
  >
    {checked && (
      <svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 5L4.5 8.5L12 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )}
  </button>
);

// Reusable "Clear Group Codes to Reassign" modal. The parent owns the commit:
// `onConfirm` receives the set of selected group_codes and decides whether to
// stage locally or commit to the backend.
export const ClearGroupCodesModal = ({
  isOpen,
  onClose,
  clearable = [],
  defaultSelectAll = false,
  onConfirm,
  saving = false,
}) => {
  const [selection, setSelection] = useState(() => new Set());

  useEffect(() => {
    if (!isOpen) return;
    setSelection(defaultSelectAll ? new Set(clearable.map((c) => c.group_code)) : new Set());
  }, [isOpen, clearable, defaultSelectAll]);

  const allChecked = useMemo(
    () => clearable.length > 0 && selection.size === clearable.length,
    [clearable, selection],
  );

  const toggleRow = (groupCode) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(groupCode)) next.delete(groupCode);
      else next.add(groupCode);
      return next;
    });
  };

  const toggleAll = () => {
    setSelection((prev) => {
      if (prev.size === clearable.length) return new Set();
      return new Set(clearable.map((c) => c.group_code));
    });
  };

  const handleConfirm = () => {
    if (selection.size === 0 || saving) return;
    onConfirm?.(selection);
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered size="lg" contentClassName="agdm-modal-content">
      <Modal.Header style={{ alignItems: "center", border: "none", display: "flex", justifyContent: "space-between", marginBottom: 24, padding: 0, width: "100%" }}>
        <Modal.Title style={{ color: "#002E19", fontSize: 24, fontWeight: 600, lineHeight: "32px" }}>
          Clear Group Codes to Reassign
        </Modal.Title>
        <button
          aria-label="Close"
          onClick={onClose}
          style={{
            alignItems: "center",
            background: "transparent",
            border: "2px solid #00140B",
            borderRadius: "100px",
            cursor: "pointer",
            display: "flex",
            height: 48,
            justifyContent: "center",
            padding: 0,
            width: 48,
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6 6 18M6 6l12 12" stroke="#00140B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </Modal.Header>
      <Modal.Body style={{ padding: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ color: "black", fontSize: 18, fontWeight: 400, letterSpacing: "0.25px", lineHeight: "26px" }}>
            Select the group codes you want to clear so they can be reassigned for the new session.
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ alignItems: "stretch", backgroundColor: "#002E19", display: "flex" }}>
              <div style={{ alignItems: "center", display: "flex", padding: "8px 16px" }}>
                <Checkbox checked={allChecked} onChange={toggleAll} onDark />
              </div>
              <div style={{ alignItems: "center", color: "white", display: "flex", fontSize: 16, fontWeight: 700, justifyContent: "center", letterSpacing: "0.25px", lineHeight: "22px", padding: "8px 16px", width: 128 }}>
                Group Code
              </div>
              <div style={{ alignItems: "center", color: "white", display: "flex", flex: "1 0 0", fontSize: 16, fontWeight: 700, letterSpacing: "0.25px", lineHeight: "22px", minWidth: 0, padding: "8px 16px" }}>
                Assigned to
              </div>
            </div>
            {clearable.map((row) => {
              const checked = selection.has(row.group_code);
              return (
                <div key={row.group_code} style={{ alignItems: "stretch", borderBottom: "1px solid #8D8D8D", display: "flex", height: 54 }}>
                  <div style={{ alignItems: "center", display: "flex", padding: "8px 16px" }}>
                    <Checkbox checked={checked} onChange={() => toggleRow(row.group_code)} />
                  </div>
                  <div style={{ alignItems: "center", color: "#002E19", display: "flex", fontSize: 16, letterSpacing: "0.25px", lineHeight: "22px", padding: "8px 16px", width: 128 }}>
                    {row.group_code}
                  </div>
                  <div style={{ alignItems: "center", color: "#002E19", display: "flex", flex: "1 0 0", fontSize: 14, lineHeight: "20px", minWidth: 0, padding: "8px 16px" }}>
                    {row.standard_code}{row.standard_name ? `: ${row.standard_name}` : ""}
                  </div>
                </div>
              );
            })}
            {clearable.length === 0 && (
              <div style={{ color: "#6B6967", padding: "16px 0" }}>No assigned group codes to clear.</div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ border: "none", padding: 0, marginTop: 8, justifyContent: "space-between" }}>
        <Button onClick={onClose} disabled={saving} style={SECONDARY_BTN_STYLE}>Back</Button>
        <Button
          disabled={selection.size === 0 || saving}
          onClick={handleConfirm}
          style={{ ...PRIMARY_BTN_STYLE(selection.size > 0 && !saving), minWidth: 180 }}
        >
          {saving
            ? "Clearing…"
            : selection.size === 0
              ? "Clear Group Codes"
              : `Clear ${selection.size} Group Code${selection.size === 1 ? "" : "s"}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
